/**
 * Tickets API hooks for HMH Lunch Ordering System.
 * Manages meal ticket creation, retrieval, and voiding operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiConfig } from '../config';

const API_BASE = apiConfig.baseUrl;

// Get auth token from storage
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('hmh_access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export interface MealTicket {
  id: string;
  ticket_code: string;
  meal_type: 'any' | 'breakfast' | 'lunch';
  status: 'active' | 'redeemed' | 'expired' | 'voided';
  redeemed_at: string | null;
  redeemed_by_name: string | null;
  created_at: string;
  expires_at: string | null;
  batch_id: string | null;
  batch_description: string | null;
  qr_code?: string;
}

export interface TicketBatch {
  tickets: MealTicket[];
  batch_id: string;
  count: number;
}

export interface CreateTicketsRequest {
  quantity: number;
  meal_type: string;
  expires_in_days?: number;
  batch_description?: string;
}

interface TicketListResponse {
  tickets: MealTicket[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * Hook to fetch tickets with optional status filter and QR codes
 */
export function useTickets(status?: string, includeQr?: boolean) {
  return useQuery({
    queryKey: ['tickets', status, includeQr],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (includeQr) params.append('include_qr', 'true');

      const queryString = params.toString();
      const url = `${API_BASE}/admin/tickets${queryString ? '?' + queryString : ''}`;

      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to fetch tickets: ${error}`);
      }
      const data: TicketListResponse = await res.json();
      return data.tickets;
    }
  });
}

/**
 * Hook to create new tickets
 */
export function useCreateTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTicketsRequest) => {
      const res = await fetch(`${API_BASE}/admin/tickets`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to create tickets: ${error}`);
      }
      return res.json() as Promise<TicketBatch>;
    },
    onSuccess: () => {
      // Invalidate all ticket queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });
}

/**
 * Hook to void a ticket
 */
export function useVoidTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) => {
      const res = await fetch(`${API_BASE}/admin/tickets/${ticketId}/void`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to void ticket: ${error}`);
      }
      return res.json() as Promise<{ message: string; ticket: MealTicket }>;
    },
    onSuccess: () => {
      // Invalidate all ticket queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });
}

/**
 * Hook to validate a ticket code (kiosk use - no auth required)
 */
export function useValidateTicket() {
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await fetch(`${API_BASE}/kiosk/tickets/validate/${code}`);
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Invalid ticket: ${error}`);
      }
      return res.json();
    }
  });
}

/**
 * Hook to fetch tickets issued to the authenticated user (e.g. birthday tickets)
 */
export function useMyTickets() {
  return useQuery({
    queryKey: ['tickets', 'mine'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/tickets/mine`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        throw new Error('Failed to fetch your tickets');
      }
      return res.json() as Promise<MealTicket[]>;
    },
    staleTime: 60_000,
  });
}

/**
 * Hook to redeem a ticket (kiosk use - no auth required)
 */
export function useRedeemTicket() {
  return useMutation({
    mutationFn: async (data: { ticket_code: string; guest_name: string }) => {
      const res = await fetch(`${API_BASE}/kiosk/tickets/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to redeem ticket: ${error}`);
      }
      return res.json();
    }
  });
}
